import { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import type { SnackbarProps } from '@crehana/ui';
import { notEmpty } from '@crehana/utils';

import { CourseB2BSourceEnum } from '@/shared/types/graphql/globals.v4.b2b';

import AddUsersCourseMutation from '../graphql/AddUsersCourseInput.v4.b2b.graphql';
import GetUsersCourseQuery from '../graphql/GetUsersCourseInput.b2b.graphql';
import type {
  AddUsersCourseInput,
  AddUsersCourseInputVariables,
} from '../graphql/types/AddUsersCourseInput';
import type {
  GetUsersCourseInput,
  GetUsersCourseInputVariables,
} from '../graphql/types/GetUsersCourseInput';
import type { BaseUser, User } from '../types';

const USERS_PER_FETCH = 10;

const useUserCourse = ({
  courseId,
  isOpen,
  onSuccess,
  organization,
}: {
  courseId: string;
  isOpen: boolean;
  onSuccess?: (isSuccess: boolean) => void;
  organization: string;
}) => {
  const { t } = useTranslation();

  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [snackbar, setSnackbar] = useState<SnackbarProps>();
  const [isSuccess, setIsSuccess] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (!isOpen) {
      setSelectedUsers([]);
      setIsSuccess(false);
      onSuccess && onSuccess(false);
    }
  }, [isOpen]);

  const onChange = (value: User[]) => setSelectedUsers(value);

  const errorToSave = () => {
    setSnackbar({
      open: true,
      message: t('SNACKBAR_DEFAULT_ERROR'),
      state: 'alert',
    });
  };

  const { refetch } = useQuery<
    GetUsersCourseInput,
    GetUsersCourseInputVariables
  >(GetUsersCourseQuery, {
    context: { clientName: 'b2b' },
    fetchPolicy: 'no-cache',
  });

  const loadUsers: (
    search: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { page }: any,
  ) => Promise<{
    options: BaseUser[];
    hasMore: boolean | undefined;
    additional: {
      page: number;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }> = async (search: string, _: any, { page }: { page: number }) => {
    const after =
      page - 1 !== 0
        ? window.btoa(`arrayconnection:${(page - 1) * USERS_PER_FETCH - 1}`)
        : '';

    const response = await refetch({
      after,
      first: USERS_PER_FETCH,
      searchKey: search,
      slug: organization,
      excludeUsersInCourseId: courseId,
    });

    return {
      options:
        response.data?.organization?.users?.edges
          .map(el => el?.node?.user)
          .filter(notEmpty) || [],
      hasMore: response.data?.organization?.users?.pageInfo.hasNextPage,
      additional: {
        page: page + 1,
      },
    };
  };

  const [addUserCourse, { loading }] = useMutation<
    AddUsersCourseInput,
    AddUsersCourseInputVariables
  >(AddUsersCourseMutation, {
    variables: {
      input: {
        userIds: selectedUsers.map(user => String(user.originalId)),
        courseId: courseId,
        organizationSlug: organization,
        source: CourseB2BSourceEnum.ADMIN,
      },
    },
    context: { clientName: 'v4.b2b' },
    onCompleted({ enrollUsersToCourse }) {
      if (enrollUsersToCourse?.success) {
        onSuccess && onSuccess(true);
        setIsSuccess(true);
        // biome-ignore lint/style/useBlockStatements: single-line early return
      } else errorToSave();
    },
    onError() {
      errorToSave();
    },
  });

  const removeUser = (id: number) => {
    const newUsers = [...selectedUsers];
    newUsers.splice(id, 1);
    setSelectedUsers(newUsers);
  };

  return {
    addUserCourse,
    loadUsers,
    isLoading: loading,
    isSuccess,
    onChange,
    selectedUsers,
    snackbar,
    setSnackbar,
    removeUser,
  };
};

export default useUserCourse;
